import React, { useEffect, useRef, useState } from 'react';
import { KronkChatbot } from './ChatbotCore';
import './styles.css';
import { Icon } from '@iconify/react';

const ChatbotWidget = ({ isOpen, onClose }) => {
  const containerRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const chatbotRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [sessionId, setSessionId] = useState('Loading...');
  const [messageCount, setMessageCount] = useState(1);
  const [apiStatus, setApiStatus] = useState('connecting');
  const [isTyping, setIsTyping] = useState(false);
  const [messageInput, setMessageInput] = useState('');

  useEffect(() => {
    if (isOpen) {
      initializeChatbot();
    } else {
      // Reset initialization state when modal closes
      setIsInitialized(false);
    }

    // Cleanup function
    return () => {
      if (containerRef.current) {
        // Remove event listeners
        containerRef.current.removeEventListener('change', handleVariantChange);
        containerRef.current.removeEventListener('click', handleProductClick);
      }
    };
  }, [isOpen]);

  // Define event handlers outside of useEffect to avoid dependency issues
  const handleVariantChange = (e) => {
    if (e.target.classList.contains('variant-select')) {
      const productId = e.target.getAttribute('data-product-id');
      if (productId && chatbotRef.current) {
        console.log('🔍 DEBUG: Variant selection changed for product:', productId);
        chatbotRef.current.updateAddToCartButton(productId);
      }
    }
  };

  const handleProductClick = (e) => {
    if (e.target.classList.contains('product-expand-btn')) {
      const productId = e.target.getAttribute('data-product-id');
      if (productId && chatbotRef.current) {
        console.log('🔍 DEBUG: Product expand clicked for product:', productId);
        chatbotRef.current.toggleProductExpansion(productId);
      }
    } else if (e.target.classList.contains('add-to-cart-btn')) {
      const productId = e.target.getAttribute('id')?.replace('addToCart-', '');
      // Extract uniqueId from the onclick attribute
      const onclickAttr = e.target.getAttribute('onclick');
      let uniqueId = productId; // fallback
      
      if (onclickAttr) {
        const match = onclickAttr.match(/window\.handleAddToCartWithVariants\('([^']+)',\s*'([^']+)'\)/);
        if (match && match[2]) {
          uniqueId = match[2];
        }
      }
      
      if (productId && chatbotRef.current) {
        console.log('🔍 DEBUG: Add to cart clicked for product:', productId, 'uniqueId:', uniqueId);
        chatbotRef.current.handleAddToCartWithVariants(productId, uniqueId);
      }
    }
  };

  // Sync React state with chatbot state
  useEffect(() => {
    if (chatbotRef.current && isInitialized) {
      // Set up interval to sync state
      const syncInterval = setInterval(() => {
        if (chatbotRef.current) {
          // Update session ID
          if (chatbotRef.current.sessionId && chatbotRef.current.sessionId !== sessionId) {
            setSessionId(chatbotRef.current.sessionId);
          }
          
          // Update message count
          if (chatbotRef.current.messageCount !== messageCount) {
            setMessageCount(chatbotRef.current.messageCount);
          }
          
          // Update typing state
          if (chatbotRef.current.isTyping !== isTyping) {
            setIsTyping(chatbotRef.current.isTyping);
          }
        }
      }, 100);

      return () => clearInterval(syncInterval);
    }
  }, [isInitialized, sessionId, messageCount, isTyping]);

  // Debug effect to monitor variant selects
  useEffect(() => {
    if (isInitialized && containerRef.current) {
      const debugInterval = setInterval(() => {
        const variantSelects = containerRef.current?.querySelectorAll('.variant-select');
        const addToCartBtns = containerRef.current?.querySelectorAll('.add-to-cart-btn');
        
        if (variantSelects.length > 0) {
          console.log('🔍 DEBUG: Found variant selects:', variantSelects.length);
          console.log('🔍 DEBUG: Found add to cart buttons:', addToCartBtns.length);
          
          // Check if event listeners are working
          variantSelects.forEach((select, index) => {
            console.log(`🔍 DEBUG: Variant select ${index}:`, {
              productId: select.getAttribute('data-product-id'),
              value: select.value,
              hasEventListener: select.onchange !== null
            });
          });
        }
      }, 2000);

      return () => clearInterval(debugInterval);
    }
  }, [isInitialized]);

  const initializeChatbot = () => {
    // Wait for the component to render before initializing
    setTimeout(() => {
      if (!containerRef.current) return;

      // Initialize the chatbot class with the container
      try {
        chatbotRef.current = new KronkChatbot(containerRef.current);
        window.currentChatbot = chatbotRef.current; // Set global reference for callbacks
        
        // Set up additional event delegation for variant selection
        setupEventDelegation();
        
        setIsInitialized(true);
        console.log('Chatbot container initialized successfully');
      } catch (error) {
        console.error('Error initializing chatbot:', error);
      }
    }, 100);
  };

  const setupEventDelegation = () => {
    if (!containerRef.current) return;

    // Add event delegation for variant selection changes
    containerRef.current.addEventListener('change', handleVariantChange);

    // Add event delegation for product interactions (expand and add to cart)
    containerRef.current.addEventListener('click', handleProductClick);
  };

  const handleSendMessage = () => {
    if (chatbotRef.current && messageInput.trim()) {
      chatbotRef.current.sendMessage(messageInput.trim());
      setMessageInput('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  const handleNewSession = () => {
    if (chatbotRef.current) {
      chatbotRef.current.newSession();
    }
  };

  const handleEndSession = () => {
    if (chatbotRef.current) {
      chatbotRef.current.endSession();
    }
  };

  const handleBackdropClick = (e) => {
    // Only close if clicking on the backdrop itself, not on the chatbot container
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleContainerClick = (e) => {
    // Prevent clicks on the chatbot container from bubbling up to the backdrop
    e.stopPropagation();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleBackdropClick}
    >
      <div className="relative w-full max-w-4xl gap-4 flex flex-col items-end justify-center">
        <div 
          ref={containerRef}
          className="w-full h-full chatbot-container"
          onClick={handleContainerClick}
        >
          {/* Chat Header */}
          <div className="chat-header">
            <h2>
              <Icon icon="noto:boxing-glove" className="text-xl" /> Kronk Boxing Equipment Chatbot
            </h2>
            <div className="session-info">
              <span>Session: <span id="sessionId">{sessionId}</span></span>
              <div className="session-info-buttons">
                <button 
                  id="newSessionBtn" 
                  className="btn-secondary"
                  onClick={handleNewSession}
                >
                  New Session
                </button>
                <button 
                  id="endSessionBtn" 
                  className="btn-danger"
                  onClick={handleEndSession}
                >
                  End Session
                </button>
              </div>
            </div>
          </div>
          
          {/* Chat Messages */}
          <div className="chat-messages scrollable" id="chatMessages" ref={chatMessagesRef}>
            <div className="message assistant">
              <div className="message-content rounded-md">
                <p>Hey there! I'm Kronk, your boxing equipment expert. How can I help you today?</p>
              </div>
              <div className="message-timestamp" id="welcomeTimestamp">
                {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </div>
          
          {/* Chat Input Container */}
          <div className="chat-input-container">
            <div className="input-wrapper">
              <input 
                type="text" 
                id="messageInput" 
                placeholder="Ask me about boxing equipment..." 
                maxLength="500"
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <button 
                id="sendButton" 
                className="btn-primary"
                onClick={handleSendMessage}
                disabled={!messageInput.trim()}
              >
                Send
              </button>
            </div>
            <div 
              className="typing-indicator" 
              id="typingIndicator" 
              style={{ display: isTyping ? 'block' : 'none' }}
            >
              <span>Kronk is typing...</span>
            </div>
          </div>
          
          {/* Chat Footer */}
          <div className="chat-footer">
            <div className="api-status">
              <span>API: <span id="apiStatus" className={`status-${apiStatus}`}>
                {apiStatus === 'connecting' ? 'Connecting...' : 
                 apiStatus === 'connected' ? 'Connected' : 
                 apiStatus === 'error' ? 'Error' : 'Unknown'}
              </span></span>
            </div>
            <div className="message-count">
              <span>Messages: <span id="messageCount">{messageCount}</span></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatbotWidget;
